import React, { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import RightPanel from './righthalf/right_half.js';
import Title from './Title.js';
import './home.css';

// Persistent shell shared by every route. The left pane (per-route content via
// <Outlet/>) and the right panel both stay mounted, so the right panel's width
// and the title's size CSS-transition smoothly on navigation. Because the right
// panel's width animates and the left pane is flex:1, the left grows/shrinks in
// lockstep — a true fluid morph that works in every browser.
const Layout = () => {
    const location = useLocation();
    const isHome = location.pathname === '/' || location.pathname === '';
    const leftRef = useRef(null);

    // The panel/title only resize on a home <-> subpage switch (subpage <->
    // subpage both sit at the same 30% width), so that's the only time text
    // reflows and needs the fade to hide it. prevIsHomeRef still holds the
    // previous route's isHome during this render (it's updated in the effect
    // below, which runs after), so comparing against it tells us which kind
    // of navigation just happened.
    const prevIsHomeRef = useRef(isHome);
    const isHomeSwitch = isHome !== prevIsHomeRef.current;

    // The left pane persists across routes, so reset its scroll on navigation.
    useEffect(() => {
        if (leftRef.current) leftRef.current.scrollTop = 0;
        prevIsHomeRef.current = isHome;
    }, [location.pathname, isHome]);

    // Wheeling (desktop) or dragging (touch) over the right panel scrolls the
    // left pane instead — centralised here now that the panel is shared
    // instead of re-created per page.
    useEffect(() => {
        const rightPanel = document.getElementById('rightpanel');
        const left = leftRef.current;
        if (!rightPanel || !left) return;

        const onWheel = (event) => {
            left.scrollTop += event.deltaY;
            event.preventDefault();
        };

        let lastTouchY = null;
        const onTouchStart = (event) => {
            lastTouchY = event.touches[0].clientY;
        };
        const onTouchMove = (event) => {
            if (lastTouchY === null) return;
            const currentY = event.touches[0].clientY;
            left.scrollTop += lastTouchY - currentY;
            lastTouchY = currentY;
            event.preventDefault();
        };
        const onTouchEnd = () => { lastTouchY = null; };

        rightPanel.addEventListener('wheel', onWheel);
        rightPanel.addEventListener('touchstart', onTouchStart, { passive: true });
        rightPanel.addEventListener('touchmove', onTouchMove, { passive: false });
        rightPanel.addEventListener('touchend', onTouchEnd);
        rightPanel.addEventListener('touchcancel', onTouchEnd);

        return () => {
            rightPanel.removeEventListener('wheel', onWheel);
            rightPanel.removeEventListener('touchstart', onTouchStart);
            rightPanel.removeEventListener('touchmove', onTouchMove);
            rightPanel.removeEventListener('touchend', onTouchEnd);
            rightPanel.removeEventListener('touchcancel', onTouchEnd);
        };
    }, []);

    // Preload the About page's large headshot/frame images once, up front, so
    // they're already cached by the time the user navigates there instead of
    // popping in.
    useEffect(() => {
        [
            require('./photos/about line longer.png'),
            require('./photos/about_border.png'),
        ].forEach((src) => {
            const img = new window.Image();
            img.src = src;
        });
    }, []);

    return (
        <div id="shell" data-home={isHome ? 'true' : 'false'}>
            <div id="leftpane" ref={leftRef}>
                <Title isHome={isHome} />
                {/* Only fades on a home <-> subpage switch, since that's the
                    only navigation where the panel resizes and reflows text. */}
                <div
                    className={"route-content" + (isHomeSwitch ? " route-content-fade" : "")}
                    key={location.pathname}
                >
                    <Outlet />
                </div>
            </div>
            <RightPanel pathname={location.pathname} />
        </div>
    );
};

export default Layout;
